class TastingNotesComponent extends HTMLElement {
  #sections = [];
  #links = [];
  #groups = [];
  #originalContents = this.innerHTML;
  #eventAbortController;
  #responsiveMode;
  #tabGroupElement = null;

  connectedCallback() {
    this.#responsiveMode = window.innerWidth >= 750 ? 'desktop' : 'mobile';
    this.#eventAbortController = new AbortController();

    this.addVintageData();

    const sectionHeadings = this.querySelectorAll('[ref="body"] .metafield-rich_text_field :is(h1, h2, h3, h4, h5, h6)');

    if (sectionHeadings.length >= 1) {
      window.addEventListener(
        "resize",
        () => requestAnimationFrame(this.handleResize.bind(this)),
        { signal: this.#eventAbortController.signal }
      );

      if (this.#responsiveMode == 'desktop') {
        this.enableTabs();
      }
    }
  }
  disconnectedCallback() {
    this.#eventAbortController?.abort();
  }

  handleResize() {
    const newResponsiveMode = window.innerWidth >= 750 ? 'desktop' : 'mobile';

    if (newResponsiveMode != this.#responsiveMode) {
      if (newResponsiveMode == 'desktop') {
        this.enableTabs();
      } else {
        this.disableTabs();
      }
      this.#responsiveMode = newResponsiveMode;
    }

    if (this.#tabGroupElement) {
      const groupHeight = Math.max(...this.#groups.map(g => g.offsetHeight));
      this.#tabGroupElement.style.minHeight = `${groupHeight}px`;
    }
  }

  disableTabs() {
    this.#sections = [];
    this.#links = [];
    this.#groups = [];
    this.#tabGroupElement = null;

    this.innerHTML = this.#originalContents;
  }
  enableTabs() {
    this.#originalContents = this.innerHTML;

    const children =
      this.querySelector('[ref="body"] .metafield-rich_text_field')
        ?.children ?? [];

    let sectionIndex = 0;
    const headingTags = ["h2"];

    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      const childIsHeading = headingTags.includes(
        child.tagName.toLowerCase(),
      );

      if (i == 0) {
        this.#sections[sectionIndex] = {
          title: "Notes",
          isFirst: true,
          preBody: [
            this.querySelector('[ref="main-title"]'),
            ...this.querySelectorAll('.product-tasting-notes__field')
          ],
          body: [child],
        };
      } else if (childIsHeading) {
        sectionIndex++;
        this.#sections[sectionIndex] = {
          title: child?.textContent,
          isFirst: false,
          preBody: [],
          body: [child],
        };
      } else {
        this.#sections[sectionIndex].body.push(child);
      }
    }
    this.innerHTML = "";

    this.populate();
  }

  populate() {
    const header = document.createElement("div");
    header.classList.add("product-tasting-notes__header");
    this.appendChild(header);

    const body = document.createElement("div");
    body.classList.add("product-tasting-notes__body-container");
    body.classList.add("product-tasting-notes__body-container--tabbed");
    this.appendChild(body);

    this.#tabGroupElement = body;

    let groupHeight = 0;

    for (let i = 0; i < this.#sections.length; i++) {
      const section = this.#sections[i];

      const tabId = this.getAttribute("section-id") + "--tab--" + i;
      const tabLinkId = this.getAttribute("section-id") + "--tab-link--" + i;

      const link = document.createElement("a");
      link.setAttribute("href", "#");
      link.setAttribute("role", "tab");
      link.setAttribute("aria-selected", "false");
      link.setAttribute("aria-controls", tabId);
      link.setAttribute("id", tabLinkId);
      link.classList.add("product-tasting-notes__header-link");
      link.textContent = section.title;
      header.append(link);

      this.#links.push(link);

      const group = document.createElement("div");
      group.setAttribute("id", tabId);
      group.setAttribute("aria-labelledby", tabLinkId);
      group.setAttribute("role", "tabpanel");
      group.classList.add("product-tasting-notes__body-tab");

      for (const element of section.preBody) {
        group.append(element);
      }

      const firstWrapper = document.createElement("div");
      firstWrapper.classList.add("product-tasting-notes__body");
      group.append(firstWrapper);

      const secondWrapper = document.createElement("div");
      secondWrapper.classList.add("metafield-rich_text_field");
      firstWrapper.append(secondWrapper);

      for (const element of section.body) {
        secondWrapper.append(element);
      }

      if (section.isFirst) {
        link.classList.add("product-tasting-notes__header-link--active");
        link.setAttribute("aria-selected", "true");
      }
      this.#groups.push(group);

      link.addEventListener("click", (e) => this.changeTab(e, i), { signal: this.#eventAbortController.signal });

      if (section.isFirst) {
        group.classList.add("product-tasting-notes__body-tab--active");
      } else {
        group.setAttribute("aria-hidden", "true");
        group.inert = true;
      }

      body.append(group);

      groupHeight = Math.max(groupHeight, group.offsetHeight);
    }
    body.style.minHeight = `${groupHeight}px`;
  }
  changeTab(event, index) {

    event.stopPropagation();
    event.preventDefault();

    for (let i = 0; i < this.#groups.length; i++) {
      if (i == index) {
        this.#groups[i].classList.add("product-tasting-notes__body-tab--active");
        this.#groups[i].setAttribute("aria-hidden", "false");
        this.#groups[i].inert = false;
      } else {
        this.#groups[i].classList.remove("product-tasting-notes__body-tab--active");
        this.#groups[i].setAttribute("aria-hidden", "true");
        this.#groups[i].inert = true;
      }
    }

    for (let i = 0; i < this.#links.length; i++) {
      if (i == index) {
        this.#links[i].classList.add("product-tasting-notes__header-link--active");
        this.#links[i].setAttribute("aria-selected", "true");

      } else {
        this.#links[i].classList.remove("product-tasting-notes__header-link--active");
        this.#links[i].setAttribute("aria-selected", "false");
      }
    }
  }

  addVintageData() {
    const raw = this.getAttribute("vintage-data");
    if (raw) {
      const vintageData = raw.split("\n");

      const fragment = document.createDocumentFragment();
      const title = document.createElement("h2");
      title.innerHTML = "Vintage <em>Data</em>";

      const ol = document.createElement("ol");


      for (const row of vintageData) {
        const [field, value] = row.split(":");
        const fieldEl = document.createElement("strong");
        fieldEl.textContent = field;

        const li = document.createElement("li");
        li.append(fieldEl, value);
        
        ol.append(li);
        
      }
      fragment.append(title, ol);

      const existingTitle = this.querySelector(".product-tasting-notes__body > div.metafield-rich_text_field > h2:not(:first-child)");

      existingTitle?.parentNode?.insertBefore(fragment, existingTitle);
    }
  }
}

if (!customElements.get("tasting-notes")) {
  customElements.define("tasting-notes", TastingNotesComponent);
}
